import React from "react";
import { ArrowLeft, ShieldAlert, ChevronRight, Filter } from "lucide-react";

// Item 13: drillthrough context bar (Builder + Viewer). Shows the dynamic page title, the chain it
// came through (A → B → C, each step clickable and state-preserving), the carried chips, the fields
// the server stripped for CLS, and a way back / clear.
export function DrillthroughBar({ drill, onClear, onBack, onStep }) {
  if (!drill) return null;
  const trail = Array.isArray(drill.trail) ? drill.trail : [];
  const mode = drill.mode || "preserve";
  return (
    <div data-testid="drillthrough-bar" role="region" aria-label="Drillthrough context"
      className="shrink-0 px-4 py-1.5 border-b border-[color:var(--glass-border)] bg-[color:rgba(24,169,135,0.08)] flex items-center gap-2 flex-wrap">
      {onBack && (
        <button type="button" data-testid="drillthrough-back" onClick={onBack} className="text-[11px] text-[color:var(--brand-ocean)] hover:underline inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" aria-hidden="true" />Back</button>
      )}
      {drill.ttl ? (
        <span data-testid="drillthrough-title" className="text-xs font-medium text-[color:var(--ink)]">{drill.ttl}</span>
      ) : null}
      {trail.length > 1 ? (
        <span data-testid="drillthrough-trail" className="flex items-center gap-0.5 text-[11px] text-[color:var(--ink-3)]">
          {trail.map((s, i) => (
            <span key={`${s.bk || i}`} className="flex items-center gap-0.5">
              {i > 0 && <ChevronRight className="w-3 h-3 opacity-60" aria-hidden="true" />}
              <button type="button" data-testid={`drillthrough-step-${i}`} onClick={() => onStep && onStep(s)}
                className="hover:underline text-[color:var(--brand-ocean)] max-w-[160px] truncate">{s.title || "Dashboard"}</button>
            </span>
          ))}
          <ChevronRight className="w-3 h-3 opacity-60" aria-hidden="true" />
          <span className="text-[color:var(--ink-2)]">here</span>
        </span>
      ) : (
        <span className="text-[11px] text-[color:var(--ink-3)]">Drillthrough from <span className="font-medium text-[color:var(--ink-2)]">{drill.from || "another dashboard"}</span></span>
      )}
      {mode === "override" && (
        <span data-testid="drillthrough-override" title="Carried filters override this dashboard's own filter defaults"
          className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[color:var(--surface)] border border-[color:var(--glass-border)] text-[color:var(--ink-3)]"><Filter className="w-2.5 h-2.5" />override</span>
      )}
      {drill.f.map((f, i) => (
        <span key={i} data-testid="drillthrough-chip" className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[color:var(--surface)] border border-[color:rgba(24,169,135,0.4)] text-[color:var(--ink-2)]">
          <span className="font-medium">{String(f.field).replace(/_/g, " ")}</span> {f.op === "in" ? `(${(f.value || []).length})` : `${f.op && f.op !== "eq" ? f.op : "="} ${Array.isArray(f.value) ? f.value.join("–") : String(f.value)}`}
        </span>
      ))}
      {Array.isArray(drill.stripped) && drill.stripped.length > 0 && (
        <span data-testid="drillthrough-stripped" title="These fields are restricted for you on this dashboard (column-level security) and were not applied"
          className="text-[10px] text-amber-600 inline-flex items-center gap-1"><ShieldAlert className="w-3 h-3" aria-hidden="true" />not carried: {drill.stripped.join(", ")}</span>
      )}
      <button data-testid="drillthrough-clear" onClick={onClear} className="text-[11px] text-[color:var(--brand-ocean)] hover:underline">Clear drillthrough</button>
    </div>
  );
}
